// REST calls for the client<->staff conversation used by the client detail's "comunicaciones" tab
// (T3 scope). Real-time delivery (SignalR) is a separate datasource
// (`../realtime/chat-signalr.service.ts`), not this one.

import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable, map } from 'rxjs';
import { ChatClientPolicyDto } from '../../dtos/chat-client-policy.dto';
import { ChatBlockDto } from '../../dtos/chat-block.dto';
import { ChatAbusePreviewDto, ChatAbuseReceiptDto } from '../../dtos/chat-abuse-report.dto';
import { ChatAbusePreviewRequest, ChatAbuseSubmission } from '../../../domain/models/chat-abuse-report.model';
import { ChatConversationDto } from '../../dtos/chat-conversation.dto';
import { ChatMessageDto } from '../../dtos/chat-message.dto';
import { ChatMessagePageDto } from '../../dtos/chat-message-page.dto';
import { ChatReadReceiptDto } from '../../dtos/chat-read-receipt.dto';

@Injectable()
export class ChatRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  public previewAbuseReport(request: ChatAbusePreviewRequest): Observable<ChatAbusePreviewDto> {
    return this.http.post<ChatAbusePreviewDto>(`${this.apiUrl}${API_ENDPOINTS.chat.abuseReportPreview}`, {
      OrganizationId: request.organizationId,
      ConversationId: request.conversationId,
      SelectedMessageIds: request.selectedMessageIds,
      IncludedAttachmentMessageIds: request.includedAttachmentMessageIds,
      Reason: request.reason,
      Explanation: request.explanation,
    });
  }

  public submitAbuseReport(request: ChatAbuseSubmission): Observable<ChatAbuseReceiptDto> {
    return this.http.post<ChatAbuseReceiptDto>(`${this.apiUrl}${API_ENDPOINTS.chat.abuseReports}`, {
      PreviewId: request.previewId,
      Version: request.version,
      IdempotencyKey: request.idempotencyKey,
    });
  }

  public getAbuseReportReceipt(reportId: string): Observable<ChatAbuseReceiptDto> {
    return this.http.get<ChatAbuseReceiptDto>(`${this.apiUrl}${API_ENDPOINTS.chat.abuseReportReceipt(reportId)}`);
  }

  public getChatBlock(organizationId: number): Observable<ChatBlockDto> {
    return this.http.get<ChatBlockDto>(`${this.apiUrl}${API_ENDPOINTS.chat.block(organizationId)}`);
  }

  public blockClinicChat(organizationId: number): Observable<ChatBlockDto> {
    return this.http.put<ChatBlockDto>(`${this.apiUrl}${API_ENDPOINTS.chat.block(organizationId)}`, null);
  }

  public unblockClinicChat(organizationId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}${API_ENDPOINTS.chat.block(organizationId)}`, { body: null }).pipe(
      map(() => undefined),
    );
  }

  // `GET /api/organizations/{organizationId}/clients/{clientId}/conversations` (`RequireStaff`,
  // `ChatController.GetOrCreateClientConversation`) finds or creates the 1:1 conversation between
  // the calling staff member and this client, returning it bare (no `PagedDto` envelope) -- same
  // "no envelope" shape as `getConsents` in `features/clients/data/datasources/remote/clients.remote-datasource.ts`.
  // `departmentId` scopes which per-department conversation is resolved/created (`undefined` = the
  // global, department-less conversation), same `HttpParams`-drops-`undefined` convention as
  // `getClinicConversations` below.
  public getOrCreateClientConversation(
    organizationId: number, clientId: string, departmentId?: number,
  ): Observable<ChatConversationDto> {
    return this.http.get<ChatConversationDto>(
      `${this.apiUrl}${API_ENDPOINTS.chat.clientConversation(organizationId, clientId)}`,
      { params: departmentId === undefined ? {} : { departmentId } },
    );
  }

  // `GET /api/chat/conversations` (`ChatController.GetMyConversations`) returns the authenticated
  // caller's own conversations (filtered server-side by their JWT `userId`), no `organizationId`/
  // `clientId` needed -- used by the mobile/tutor side instead of `getOrCreateClientConversation`,
  // which is staff-only (`RequireStaff`, clinic-scoped).
  public getMyConversations(): Observable<readonly ChatConversationDto[]> {
    return this.http.get<readonly ChatConversationDto[]>(`${this.apiUrl}${API_ENDPOINTS.chat.myConversations}`);
  }

  // `GET /api/chat/clinic-conversations` (`RequireStaff`, `ChatController.GetClinicConversations`)
  // -- the staff inbox/mailbox: every conversation for the caller's own clinic, resolved
  // server-side from their JWT (no route params). Used by the new `ClinicConversationsStore`
  // driving `CommunicationPage`'s conversation list.
  public getClinicConversations(departmentId?: number): Observable<readonly ChatConversationDto[]> {
    return this.http.get<readonly ChatConversationDto[]>(`${this.apiUrl}${API_ENDPOINTS.chat.clinicConversations}`, {
      params: departmentId === undefined ? {} : { departmentId },
    });
  }

  // `GET /api/chat/conversations/{conversationId}/messages?beforeMessageId=&pageSize=`
  // (`ChatController.GetMessages`) returns a `ChatMessagePageDto` envelope, newest messages first
  // internally (keyset/cursor pagination, `WHERE Id < beforeMessageId`) -- reversed to chronological
  // order by the repository before reaching the domain layer. `beforeMessageId` omitted (`null`)
  // fetches the most recent `pageSize` messages (initial load); passing the oldest-loaded message's
  // id fetches the next-older page for "Cargar más histórico". `HttpParams`-backed `params` drops a
  // key entirely when its value is `undefined` (unlike `null`, which would be serialized as the
  // literal string `"null"`), matching the backend's `long?` optional semantics.
  public getMessages(
    conversationId: string, beforeMessageId: string | null, pageSize: number,
  ): Observable<ChatMessagePageDto> {
    return this.http.get<ChatMessagePageDto>(
      `${this.apiUrl}${API_ENDPOINTS.chat.messages(conversationId)}`,
      { params: { pageSize, ...(beforeMessageId !== null ? { beforeMessageId } : {}) } },
    );
  }

  // `POST /api/chat/conversations/{conversationId}/messages` (`ChatController.SendMessage`)
  // returns the persisted `ChatMessageDto` (201 Created). `clientMessageId` is generated by the
  // caller (store) for optimistic-send deduplication against the SignalR echo of this same
  // message.
  public sendMessage(conversationId: string, body: string, clientMessageId: string): Observable<ChatMessageDto> {
    return this.http.post<ChatMessageDto>(
      `${this.apiUrl}${API_ENDPOINTS.chat.messages(conversationId)}`,
      { Body: body, ClientMessageId: clientMessageId },
    );
  }

  // `POST /api/chat/my-conversation/messages` (`RequireStaffOrClient`, in practice only called by
  // the `Client` role -- `ChatController.SendMyFirstMessage`) resolves/creates the caller's own
  // PER-DEPARTMENT conversation from their JWT and sends the message in the same call, returning
  // the persisted `ChatMessageDto` (201 Created) with `ConversationId` set to the resolved/created
  // conversation -- used when the tutor has no conversation yet for this department
  // (`MyConversationStore`'s `'empty'` status). `departmentId` is required: a tutor can have one
  // conversation per department, and the backend resolves/creates strictly by
  // `(OrganizationId, ClientUserId, DepartmentId)`, so omitting it would silently reuse/create the
  // wrong conversation for whichever department screen the tutor happens to have open.
  public sendFirstMessage(body: string, clientMessageId: string, departmentId: number): Observable<ChatMessageDto> {
    return this.http.post<ChatMessageDto>(
      `${this.apiUrl}${API_ENDPOINTS.chat.myConversationMessages}`,
      { Body: body, ClientMessageId: clientMessageId, DepartmentId: departmentId },
    );
  }

  // `POST /api/chat/conversations/{conversationId}/read` (`ChatController.MarkAsRead`) returns a
  // `ChatReadReceiptDto` (200 OK), which the repository maps to `void` -- the REST response body
  // itself is not consumed further up; the read-receipt UI is instead driven live by the
  // `ConversationRead` SignalR event (see `ChatSignalRService.conversationRead` /
  // `ClinicConversationDetailStore`'s `onInit` hook), not by this call's response.
  public markAsRead(conversationId: string, lastReadMessageId: string): Observable<ChatReadReceiptDto> {
    return this.http.post<ChatReadReceiptDto>(
      `${this.apiUrl}${API_ENDPOINTS.chat.markAsRead(conversationId)}`,
      { LastReadMessageId: Number(lastReadMessageId) },
    );
  }

  // `POST /api/chat/conversations/{conversationId}/clinical-requests/seen` (`ChatController
  // .MarkClinicalRequestsSeen`) stamps a new server-side "seen" cursor for the caller's own
  // participant row (204 No Content, no body) -- completely separate from `markAsRead` above, which
  // requires a real message id and cannot be reused here: resolving a receta/informe request posts
  // no chat message, so there is nothing for that endpoint's cursor to point at. `null` body, same
  // POST-with-no-body convention as `hideConversation`/`markAttachmentViewed` below.
  public markClinicalRequestsSeen(conversationId: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}${API_ENDPOINTS.chat.markClinicalRequestsSeen(conversationId)}`, null);
  }

  // `POST /api/chat/clinic-conversations/{conversationId}/hide` (`RequireStaff`,
  // `ChatController.HideClinicConversation`) hides the conversation from the caller's own clinic
  // inbox (204 No Content, no body) -- the backend resolves `organizationId` from the caller's own JWT,
  // so no route/query param for it is needed here. It reappears on its own the next time
  // `getClinicConversations` is called after a later message lands (see `GetClinicConversationsAsync`),
  // no separate "unhide" call exists.
  public hideConversation(conversationId: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}${API_ENDPOINTS.chat.hideClinicConversation(conversationId)}`, null);
  }

  // `POST /api/chat/conversations/{conversationId}/attachments` (`ChatController.UploadAttachment`,
  // `[Authorize(Policy="RequireStaffOrClient")]`) -- multipart upload, `attachment` is the exact
  // form-field name the backend's `IFormFile? attachment` parameter binds to (mirrors
  // `PatientDocumentsRemoteDatasource.upload`'s `formData.append('file', file)` pattern, just with
  // the field name this endpoint actually expects). `clientMessageId` is likewise appended as a
  // `clientMessageId` form field, bound by `[FromForm] string? clientMessageId` server-side, so the
  // optimistic-send dedup against the SignalR echo works the same way `sendMessage`'s JSON body
  // field does. Returns the persisted `ChatMessageDto` (200 OK) with `Attachment*` fields populated
  // -- same response shape `sendMessage` returns for text. `RequestSizeLimit` (25MB) and accepted
  // content types (`application/pdf`, `text/plain`, `image/png`, `image/jpeg`, plus the voice-message
  // types `audio/webm`/`audio/ogg`/`audio/mp4`) are enforced server-side; this datasource does not
  // duplicate that validation. `durationSeconds` is appended as a `durationSeconds` form field
  // (whole seconds, bound by `[FromForm] int? durationSeconds`) only when provided -- voice-message
  // uploads only, every other attachment kind omits it entirely.
  public sendAttachment(
    conversationId: string, file: File, clientMessageId: string, durationSeconds?: number,
  ): Observable<ChatMessageDto> {
    const formData = new FormData();
    formData.append('attachment', file);
    formData.append('clientMessageId', clientMessageId);
    if (durationSeconds !== undefined) formData.append('durationSeconds', String(durationSeconds));
    return this.http.post<ChatMessageDto>(
      `${this.apiUrl}${API_ENDPOINTS.chat.attachments(conversationId)}`,
      formData,
    );
  }

  // `GET /api/chat/attachments/{documentId:guid}/download` (`ChatController.DownloadAttachment`,
  // `[Authorize(Policy="RequireStaffOrClient")]`) proxy-streams the raw bytes (no JSON envelope),
  // hence `responseType: 'blob'` -- same shape as `PatientDocumentsRemoteDatasource.download`.
  // `documentId` here IS `Document.PublicId` (a `Guid`), matching `Message.attachment
  // .documentPublicId` directly -- kept typed as `string | number` (not narrowed to
  // `documentPublicId`) since this same endpoint is expected to be reused by the client
  // "Documentos" tab in a follow-up task.
  public downloadAttachment(documentId: string | number): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}${API_ENDPOINTS.chat.downloadAttachment(documentId)}`,
      { responseType: 'blob' },
    );
  }

  // `POST /api/chat/attachments/{documentId}/viewed` (`ChatController.MarkAttachmentViewed`,
  // `[Authorize(Policy="RequireStaffOrClient")]`) -- `documentId` here IS the `Guid` `PublicId`
  // (route constrained `{documentId:guid}`, deliberately different from `downloadAttachment`'s
  // `:long` route above), so `Message.attachment.documentPublicId` can be passed directly. 204 No
  // Content on success.
  public markAttachmentViewed(documentPublicId: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}${API_ENDPOINTS.chat.markAttachmentViewed(documentPublicId)}`, null);
  }

  // `GET /api/chat/my-policy` (`RequireStaffOrClient`, in practice only meaningful for the `Client`
  // role -- `ChatController.GetMyPolicy`) resolves the caller's own organization's
  // `ClientChatAudioEnabled`/`ClientChatFilesEnabled` toggles (`OrganizationProfile`, configurable
  // in Ajustes > Comunicación web). Advisory only -- it exists so the tutor composer can hide the
  // mic/attach controls up front instead of letting a send silently 403; the backend still
  // enforces the same toggles server-side on every `sendAttachment` call regardless of what this
  // endpoint reports.
  public getMyChatPolicy(): Observable<ChatClientPolicyDto> {
    return this.http.get<ChatClientPolicyDto>(`${this.apiUrl}${API_ENDPOINTS.chat.myPolicy}`);
  }
}
